import Cookies from "js-cookie";
import {
    createContext,
    useContext,
    useEffect,
    useMemo,
    useRef,
    useState,
} from "react";
import { useLocation } from "react-router-dom";
import { getMyPermissions } from "../services/me/getMyPermissions";

const PermissionsContext = createContext({
    permissions: new Set(),
    can: () => false,
    permissionsLoading: false,
    refreshPermissions: async () => {},
});

export function PermissionsProvider({ children }) {
    const location = useLocation();
    const [permissions, setPermissions] = useState(new Set());
    const [permissionsLoading, setPermissionsLoading] = useState(() =>
        Boolean(Cookies.get("Bearer")),
    );
    const tokenRef = useRef(null);

    const loadPermissions = async () => {
        const token = Cookies.get("Bearer");

        if (!token) {
            tokenRef.current = null;
            setPermissions(new Set());
            setPermissionsLoading(false);
            return;
        }

        setPermissionsLoading(true);
        try {
            const response = await getMyPermissions();

            if (response.success) {
                const normalizedPermissions = (response.permissionKeys || []).map(
                    (permissionKey) =>
                        String(permissionKey || "").trim().toLowerCase(),
                );
                setPermissions(new Set(normalizedPermissions));
                tokenRef.current = token;
            } else {
                // Never retain permissions from a previous session after a failed
                // refresh (including errors other than an expired token).
                setPermissions(new Set());
                if (response.status === 401) {
                    tokenRef.current = null;
                }
            }
        } catch (error) {
            console.error("Error loading permissions:", error);
            setPermissions(new Set());
        } finally {
            setPermissionsLoading(false);
        }
    };

    useEffect(() => {
        const token = Cookies.get("Bearer");

        if (!token) {
            if (tokenRef.current !== null || permissions.size > 0) {
                tokenRef.current = null;
                setPermissions(new Set());
            }
            setPermissionsLoading(false);
            return;
        }

        loadPermissions();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [location.pathname]);

    const value = useMemo(() => {
        return {
            permissions,
            can: (permissionKey) =>
                permissions.has(
                    String(permissionKey || "").trim().toLowerCase(),
                ),
            permissionsLoading,
            refreshPermissions: loadPermissions,
        };
    }, [permissions, permissionsLoading]);

    return (
        <PermissionsContext.Provider value={value}>
            {children}
        </PermissionsContext.Provider>
    );
}

export const usePermissions = () => useContext(PermissionsContext);
